import React, { createContext, useState, useContext, useMemo } from 'react';

import { GetPoolsList } from 'features/pages/portfolio/CashFlowModeling/__generated__/getListingsList_gql';
import { GetListings } from 'query/__generated__/getListings';
import { GetPools } from 'query/__generated__/getPools';
// import type: PortfolioSummary.tsx imports usePortfolioContext from this file, so a plain
// value-level import here would be circular; type-only imports are erased and safe.
import type { PortfolioSummaryNumericSnapshot } from './PortfolioSummary';

type Pool = NonNullable<
  NonNullable<NonNullable<NonNullable<GetPoolsList>['user']>['company']>['pools']
>[number];
type Listing = NonNullable<
  NonNullable<NonNullable<NonNullable<GetListings>['user']>['company']>['listings']
>[number];
type PoolManagerPool = NonNullable<
  NonNullable<NonNullable<NonNullable<GetPools>['user']>['company']>['pools']
>[number];
type PortfolioContextType = {
  selectedPoolsData: SelectedPoolData[];
  setSelectedPoolsData: (v: SelectedPoolData[]) => void;
  totalFilteredPoolData: PortfolioSummaryNumericSnapshot | null;
  setTotalFilteredPoolData: (v: PortfolioSummaryNumericSnapshot | null) => void;
};

export const PortfolioContext = createContext<PortfolioContextType | null>(null);

export const usePortfolioContext = (): PortfolioContextType => {
  const portfolioContext = useContext(PortfolioContext);

  if (!portfolioContext) {
    throw new Error('portfolioContext must be used within a PortfolioContextProvider');
  }

  return portfolioContext;
};

export interface SelectedPoolData {
  id: string;
  name: string;
  unpaidBalance: number | null;
  loanCount: number | null;
  averageAge: number | null;
  averageMaturity: number | null;
  netCoupon: number | null;
  fico: number | null;
  ltv: number | null;
  dti: number | null;
  minDscr?: number | null;
  maxDscr?: number | null;
  avgDscr?: number | null;
  dscrCount?: number | null;
  nonQmCount?: number | null;
  dscrPct?: number | null;
  nonQmPct?: number | null;
  assetUtilCount?: number | null;
  assetUtilPct?: number | null;
  // NOTE: those types added for compatibility with the original generateMetrics param type.
  // Will be reviewed and simplified in the future.
  originalBalance?: null;
  averageBalance?: null;
}

export type SelectablePool = Listing | Pool | PoolManagerPool;

export type SelectedPoolDscrMetrics = {
  minDscr?: number | null;
  maxDscr?: number | null;
  avgDscr?: number | null;
  dscrCount?: number | null;
  nonQmCount?: number | null;
  dscrPct?: number | null;
  nonQmPct?: number | null;
  assetUtilCount?: number | null;
  assetUtilPct?: number | null;
};

export const transformSelectedPoolsData = (
  selectedPoolsData: SelectablePool[],
): SelectedPoolData[] => {
  return selectedPoolsData.map((pool) => ({
    id: pool.id,
    name: pool.name,
    unpaidBalance: pool.t_current_balance_cents,
    loanCount: pool.loan_count,
    averageAge: pool.wa_loan_age_months,
    averageMaturity: pool.wa_remaining_loan_terms_months,
    netCoupon: pool.wa_coupon_bps,
    fico: pool.wa_borrower_credit_score,
    ltv: pool.wa_ltv_bps,
    dti: pool.wa_dti_bps,
    minDscr: null,
    maxDscr: null,
    avgDscr: null,
    dscrCount: null,
    nonQmCount: null,
    dscrPct: null,
    nonQmPct: null,
    assetUtilCount: null,
    assetUtilPct: null,
  }));
};

export const mergeSelectedPoolDscrMetrics = (
  base: SelectedPoolData,
  dscr: SelectedPoolDscrMetrics,
): SelectedPoolData => ({
  ...base,
  minDscr: dscr.minDscr ?? null,
  maxDscr: dscr.maxDscr ?? null,
  avgDscr: dscr.avgDscr ?? null,
  dscrCount: dscr.dscrCount ?? null,
  nonQmCount: dscr.nonQmCount ?? null,
  dscrPct: dscr.dscrPct ?? null,
  nonQmPct: dscr.nonQmPct ?? null,
  assetUtilCount: dscr.assetUtilCount ?? null,
  assetUtilPct: dscr.assetUtilPct ?? null,
});

export const PortfolioContextProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [selectedPoolsData, setSelectedPoolsData] = useState<SelectedPoolData[]>([]);
  const [totalFilteredPoolData, setTotalFilteredPoolData] =
    useState<PortfolioSummaryNumericSnapshot | null>(null);
  const value = useMemo(() => {
    return {
      selectedPoolsData,
      setSelectedPoolsData,
      totalFilteredPoolData,
      setTotalFilteredPoolData,
    };
  }, [selectedPoolsData, totalFilteredPoolData]);

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
};
